import { ActionIcon, Badge, Button, Image, Tooltip } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import dayjs from "dayjs";
import { toast } from "react-toastify";

import truckIcon from "@/assets/images/icons/truck.svg";
import soundIcon from "@/assets/sound-icon.svg";
import { Wrapper } from "@/components/@custom/@utils/wrapper";
import { Plate } from "@/components/@custom/Plate";
import { RoleGuard } from "@/components/@custom/RoleGuard";
import { useSession } from "@/hooks/@custom/useSession";
import { QueueCompany, useGetSettingsByCompanyQuery } from "@/store/apis/nextcargo";
import { cn, isArray } from "@/utils";

import { ToleranceBadge } from "../ToleranceBadge";
import { TabActionsContextV2Props } from "./ExpeditionTabV2.context";
import { useExpeditionTabContextV2 } from "./ExpeditionTabV2.hooks";

export type ExpeditionTabRowV2Props = {
  item: QueueCompany;
  options?: {
    color?: string;
  };
} & Pick<TabActionsContextV2Props, "actions">;
export function ExpeditionTabRowV2({ item, actions = [], options }: ExpeditionTabRowV2Props) {
  const isMobile = useMediaQuery("(max-width: 640px)");
  const { actions: ctxActions, handleShipping: onShipping, handleCheckInCall: onCall } = useExpeditionTabContextV2();

  const actionsToArr = () => {
    if (ctxActions) {
      return isArray(ctxActions) ? ctxActions : [ctxActions];
    }
    return isArray(actions) ? actions : [actions];
  };
  const effectiveActions = actionsToArr();

  const { expedition } = useSession();
  const companyId = expedition?.company.id;
  const { data, refetch } = useGetSettingsByCompanyQuery({ id: companyId! }, { skip: !companyId });

  const date = dayjs(item.date).format("HH:mm");
  const time = dayjs(date, "HH:mm").format("HH:mm");

  const isInTolerance = data?.enableTolerance;
  const driverCheckinActive = item.isCheckin;
  const checkInActive = !data?.enableCheckin || driverCheckinActive;
  const isFreelancer = item.scheduling === null;

  const getThemeColor = () => {
    if (driverCheckinActive) {
      return options?.color || "text-[#228be6]";
    }

    if (isInTolerance && data?.$toleranceQueueIds.includes(item.id)) {
      return "text-[#fc8f00]";
    }

    return "text-[#de2a1c]";
  };

  const themeColor = getThemeColor();

  const handleShipping = async () => {
    await refetch();

    if (!checkInActive) return toast.warn("Motorista precisa fazer check-in!");

    onShipping(item);
  };

  const handleCall = () => {
    console.log(item.checkinId);

    if (!checkInActive) return;
    if (!item.checkinId) return;

    onCall({ id: item.checkinId, isCalling: true });
  };

  return (
    <div className="flex flex-col justify-between gap-2 border-b border-dashed border-gray-300 px-2 py-3 last:border-b-0 sm:flex-row md:items-center md:gap-4 md:px-4 md:py-4">
      <div className="xs:flex-row flex flex-1 flex-col-reverse items-center gap-[inherit]">
        <div className="flex gap-[inherit]">
          <div className={cn("flex w-full max-w-12 flex-col rounded border-2 border-solid border-current text-center", themeColor)} style={{ color: themeColor }}>
            <div className={cn("bg-current px-2 py-1")}>
              <p className="text-xs font-bold text-white">{item.position}°</p>
            </div>
            <p className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700">{time}</p>
          </div>

          <Plate
            plate={item.truck.licensePlate}
            color={themeColor}
            slots={{
              wrapper: { className: "w-40 shrink-0 sm:w-44" }
            }}
          />
        </div>

        <div className="flex flex-col gap-1">
          <div className="flex gap-[inherit]">
            {!isFreelancer && (
              <Badge color="red.8" radius="sm" c="white" classNames={{ label: cn("font-semibold! capitalize!") }}>
                Pedido
              </Badge>
            )}

            {isFreelancer && (
              <Badge color="gray.4" radius="sm" c="black" classNames={{ label: cn("font-semibold! capitalize!") }}>
                Autônomo
              </Badge>
            )}

            {isInTolerance && <ToleranceBadge queue={item} />}
          </div>

          <p className="text-sm font-medium text-gray-800">{item.driver.name}</p>
        </div>
      </div>

      <RoleGuard notAllowed={[RoleGuard.Role.Espectador, RoleGuard.Role.Portaria]}>
        {(effectiveActions?.includes("shipping") || effectiveActions?.includes("call")) && (
          <div className={cn("flex gap-1")}>
            {effectiveActions.includes("shipping") && (
              <>
                <Tooltip label="Iniciar carregamento" target={`#shipping-button-${item.id}`} color="yellow" hidden={isMobile} />
                <Wrapper.When
                  active={isMobile}
                  components={{ active: Button, inactive: ActionIcon }}
                  slots={{
                    active: {
                      fullWidth: true,
                      size: "sm",
                      radius: "sm"
                    },
                    inactive: {
                      radius: "xl",
                      size: "xl"
                    },
                    both: {
                      color: "yellow",
                      onClick: handleShipping,
                      id: `shipping-button-${item.id}`
                    }
                  }}
                >
                  <Image src={truckIcon} alt="Caminhão" className="size-5!" />
                </Wrapper.When>
              </>
            )}

            {effectiveActions.includes("call") && (
              <>
                <Tooltip label="Chamar motorista" target={`#call-button-${item.id}`} color="green" hidden={isMobile} />
                <Wrapper.When
                  active={isMobile}
                  components={{ active: Button, inactive: ActionIcon }}
                  slots={{
                    active: {
                      fullWidth: true,
                      size: "sm",
                      radius: "sm"
                    },
                    inactive: {
                      radius: "xl",
                      size: "xl"
                    },
                    both: {
                      color: "green",
                      onClick: handleCall,
                      id: `call-button-${item.id}`
                    }
                  }}
                >
                  <img src={soundIcon} alt="Chamar" className="size-5" />
                </Wrapper.When>
              </>
            )}
          </div>
        )}
      </RoleGuard>
    </div>
  );
}
